import { Link } from '@cluey/cluey-components';
import { useLocation } from 'react-router-dom';

import { FAQQuestions } from '../../components/FAQComponent/types';
import { scrollToAnchor } from '../../util/helpers';
import { PLAN_DETAILS_SECTION_ID } from '../../views/account/PricingPlanDetailsPage/components/PricingPlanDetailsSection/PricingPlanDetailsSection';
import { PATH_HUB_PLAN_DETAILS } from '../pagePath';

// const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/';

const PlanDetailsLink = ({ label }: { label: string }) => {
  const { pathname } = useLocation();
  const onPlanDetailsPage = pathname === PATH_HUB_PLAN_DETAILS;

  if (onPlanDetailsPage) {
    return (
      <span className="cursor-pointer text-blue-5 underline" onClick={() => scrollToAnchor(PLAN_DETAILS_SECTION_ID)}>
        {label}
      </span>
    );
  }

  return (
    <Link to={`${PATH_HUB_PLAN_DETAILS}#${PLAN_DETAILS_SECTION_ID}`} className="text-blue-5 underline">
      {label}
    </Link>
  );
};

export const generalPlanQuestions: FAQQuestions = {
  id: ['general-plan'],
  questions: {
    howAmICharged: {
      question: 'How am I charged for my Cluey plan?',
      priority: 1,
      answer: (
        <div className="space-y-2">
          <div>
            Your registered payment method is charged according to your selected plan. When you first sign up,
            you&apos;ll be charged upfront for your initial sessions.
          </div>
          <div>
            After your first session, ongoing payments are charged automatically each week to ensure your account
            remains a set number of sessions in advance, based on <PlanDetailsLink label="your plan" />.
          </div>
        </div>
      ),
    },
    howMuchAmICharged: {
      question: 'How much am I going to be charged?',
      priority: 2,
      answer: (
        <div className="space-y-2">
          <div>
            You can check your upcoming payment amount and next billing date anytime on your{' '}
            <PlanDetailsLink label="Plan Details" /> page.
          </div>
          <div>
            Your weekly payment is based on your session price and maintaining your plan&apos;s required number of
            sessions in advance.
          </div>
        </div>
      ),
    },
    canICancelFreeOfCharge: {
      question: 'Am I allowed to cancel any session free of charge',
      priority: 3,
      answer: (
        <div className="space-y-2">
          <div>Once paid, a session needs to be used before the next scheduled session. </div>
          <div>No credit will be applied to the account if the student misses the session.</div>
          <div>
            However, you can still reschedule a session if needed, provided you do so at least 4 hours before the
            session&apos;s scheduled start time.
          </div>

          <div>
            To change or reschedule a session, navigate to the session you wish to update in your Hub account and select
            &apos;change&apos;.
          </div>
        </div>
      ),
    },
    canIReschedule: {
      question: 'Can I reschedule a session?',
      priority: 4,
      answer: (
        <div className="space-y-2">
          <div>
            Yes — you can reschedule a one-to-one session free of charge up to 4 hours before the scheduled start time.
            To do so, navigate to the session you wish to reschedule in your Hub account and select &apos;change&apos;.
            Select the option to reschedule the session and choose a new day and time that works best for you.
          </div>
          <div>Please note: </div>
          <div>
            <ul className="list-inside list-disc space-y-3">
              <li>Rescheduled sessions must take place before your next scheduled session</li>
              <li>If your regular tutor is unavailable, a replacement tutor may be offered</li>
              <li>Each session can only be rescheduled once</li>
            </ul>
          </div>
        </div>
      ),
    },
    canIPause: {
      question: 'Can I pause my sessions?',
      priority: 5,
      answer: (
        <div className="space-y-2">
          <div>Pausing is available during summer public school holidays in your state.</div>
          <div>During other school holidays:</div>
          <div>
            <ul className="list-inside list-disc space-y-3">
              <li>You can cancel sessions with at least 1 day’s notice</li>
              <li>You will receive a make-up voucher to use for a future session</li>
            </ul>
          </div>
          <div>Make-up vouchers must be used before their expiry date.</div>
        </div>
      ),
    },
    myChildIsSick: {
      question: "What happens if my child gets sick and can't attend the session?",
      answer: (
        <div className="space-y-2">
          <div>
            You can reschedule the session up to 4 hours before the scheduled start time via your Hub. To do so,
            navigate to the session you wish to reschedule in your Hub account and select &apos;change&apos;. Select the
            option to reschedule the session and choose a new day and time that works best for you.
          </div>
        </div>
      ),
    },
    whatIfWeAreLate: {
      question: "What should I do if we are running late and can't connect to a session on time?",
      answer: (
        <div className="space-y-2">
          <div>Tutors will wait up to 25 minutes from the scheduled start time.</div>
          <div>Sessions will still end at the scheduled time and cannot be extended.</div>
          <div>
            If you are not in the session at the start time, you will receive an automatic call from us. If you are
            having technical difficulties, be sure to answer this call so we can help. You can also contact support via
            chat from the Hub or Learning Platform.
          </div>
        </div>
      ),
    },
    whatIfTheTutorNoShows: {
      question: 'What is the policy if the tutor fails to attend a session?',
      answer:
        'If a tutor cannot attend a session and we cannot arrange a replacement tutor, you will receive a free session voucher which can be used to add another session on any of your active enrolments.',
    },
    doIGetCreditForCancelledSessions: {
      question: 'Do I get a credit if Cluey cancels a session?',
      answer:
        'There are rare instances where we may need to cancel a session, such as a last-minute tutor cancellation when a replacement cannot be found or in the event of a technical outage. In such cases, we will provide you with a free session voucher which can be used to add another session on any of your active enrolments.',
    },
    howCanIChangeMyPlan: {
      question: 'How can I change my plan?',
      answer:
        'You can change your plan by clicking on the ‘Change plan’ button at the bottom of your Plan Details page. Please note: the new price will take effect from the next unpaid session. Sessions you have already paid for will not be impacted.',
    },
    whatHappensToBilling: {
      question: 'What happens to my billing if I change my plan?',
      answer: (
        <div className="space-y-2">
          <div>Your billing cycle will continue as normal.</div>
          <div>Once your new plan takes effect, your weekly payments will reflect your updated plan.</div>
          <div>Any changes will take effect after your currently paid sessions have been used.</div>
        </div>
      ),
    },
    doIReceiveAdditionalSessions: {
      question: 'Do I receive any additional sessions or rewards?',
      answer: (
        <div className="space-y-2">
          <div>
            Depending on your plan, you may receive Loyalty-based rewards issued after completing a defined number of
            sessions.
          </div>
          <div>These benefits:</div>
          <div>
            <ul className="list-inside list-disc space-y-3">
              <li>Can be used to book additional sessions</li>
              <li>Must be used within their validity period</li>
            </ul>
          </div>
        </div>
      ),
    },
  },
};

export const saverPlusPlanQuestions: FAQQuestions = {
  title: 'Saver Plus',
  questions: {
    howMuchAmICharged: {
      question: 'How much am I going to be charged?',
      priority: 2,
      answer:
        'You can check the amount and the date of the next payment on your Plan Details page. Your ongoing weekly charge with Cluey is based on maintaining four sessions in advance.',
    },
    canICancelFreeOfCharge: {
      question: 'Am I allowed to cancel any session free of charge?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              You cannot cancel a session free of charge on the Saver Plus plan. Once paid, a session needs to be used
              before the next scheduled session.
            </li>
            <li>No credit will be applied to the account if the student misses the session.</li>
            <li>
              However, you can still reschedule a session if needed, provided you do so at least 4 hours before the
              session's scheduled start time.
            </li>
          </ul>
        </div>
      ),
    },
    howDoIPause: {
      question: 'How do I pause an enrolment?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              Pausing sessions is only available during the Summer public school holidays in your state of residence.
              The pause option will be available in your Hub 4 weeks before the start of the Summer public school
              holidays.
            </li>
            <li>
              During the Autumn, Winter, Spring public school holidays, you can opt to cancel sessions with 1 day's
              notice to receive a Cluey Voucher which you can use to book in a make-up session for any active enrolment
              on your account.
            </li>
            <li>
              You must use the Cluey voucher by its expiry date (75 days from the date of the original session) or it
              will be forfeited.
            </li>
          </ul>
        </div>
      ),
    },
  },
};

export const saverPlanQuestions: FAQQuestions = {
  title: 'Saver',
  questions: {
    howMuchAmICharged: {
      question: 'How much am I going to be charged?',
      priority: 2,
      answer:
        'You can check the amount and the date of the next payment on your Plan Details page. Your ongoing weekly charge with Cluey is based on maintaining two sessions in advance.',
    },
    canICancelFreeOfCharge: {
      question: 'Am I allowed to cancel any session free of charge?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              You cannot cancel a session free of charge on the Saver plan. Once paid, a session needs to be used before
              the next scheduled session.
            </li>
            <li>No credit will be applied to the account if the student misses the session.</li>
            <li>
              However, you can still reschedule a session if needed, provided you do so at least 4 hours before the
              session's scheduled start time.
            </li>
          </ul>
        </div>
      ),
    },
    howDoIPause: {
      question: 'How do I pause an enrolment?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              Pausing sessions is only available during the Summer public school holidays in your state of residence.
              The pause option will be available in your Hub 4 weeks before the start of the Summer public school
              holidays.
            </li>
            <li>
              During the Autumn, Winter, Spring public school holidays, you can opt to cancel sessions with 1 day's
              notice to receive a Cluey Voucher which you can use to book in a make-up session for any active enrolment
              on your account.
            </li>
            <li>
              You must use the Cluey voucher by its expiry date (75 days from the date of the original session) or it
              will be forfeited.
            </li>
          </ul>
        </div>
      ),
    },
  },
};

export const flexiPlanQuestions: FAQQuestions = {
  title: 'Flexi',
  questions: {
    howMuchAmICharged: {
      question: 'How much am I going to be charged?',
      priority: 2,
      answer:
        'You can check the amount and the date of the next payment on your Plan Details page. Your ongoing weekly charge with Cluey is based on maintaining two sessions in advance.',
    },
    canICancelFreeOfCharge: {
      question: 'Am I allowed to cancel any session free of charge?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              You can cancel any session up to 48 hours before the session's scheduled start time and get a full credit
              to use for a future session.
            </li>
            <li>Sessions that are cancelled within 48 hours are not eligible for a credit.</li>
            <li>
              You can also reschedule a session rather than cancel it, provided you do so at least 4 hours before the
              session's scheduled start time.
            </li>
            <li>
              To change or cancel a session, navigate to the session you wish to cancel in your Hub account and select
              'change.' You can select the cancel option or reschedule the session for a new day and time that works
              best for you.
            </li>
          </ul>
        </div>
      ),
    },
    howDoIPause: {
      question: 'How do I pause an enrolment?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>You can pause sessions anytime for a period of up to 4 weeks.</li>
            <li>Additionally, you can pause during the summer holidays.</li>
          </ul>
        </div>
      ),
    },
  },
};

export const valuePlusPlanQuestions: FAQQuestions = {
  title: 'Value Max',
  questions: {
    howMuchAmICharged: {
      question: 'How much am I going to be charged?',
      priority: 2,
      answer:
        'You can check the amount and the date of the next payment on your Plan Details page. Your ongoing weekly charge with Cluey is based on maintaining six sessions in advance.',
    },
    canICancelFreeOfCharge: {
      question: 'Am I allowed to cancel any session free of charge?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              You cannot cancel a session free of charge on the Value Max plan. Once paid, a session needs to be used
              before the next scheduled session.
            </li>
            <li>No credit will be applied to the account if the student misses the session.</li>
            <li>
              However, you can still reschedule a session if needed, provided you do so at least 4 hours before the
              session's scheduled start time.
            </li>
          </ul>
        </div>
      ),
    },
    howDoIPause: {
      question: 'How do I pause an enrolment?',
      priority: 2,
      answer: (
        <div>
          <ul className="list-inside list-disc space-y-4">
            <li>
              Pausing sessions is only available during the Summer public school holidays in your state of residence.
              The pause option will be available in your Hub 4 weeks before the start of the Summer public school
              holidays.
            </li>
            <li>
              During the Autumn, Winter, Spring public school holidays, you can opt to cancel sessions with 1 day's
              notice to receive a Cluey Voucher which you can use to book in a make-up session for any active enrolment
              on your account.
            </li>
            <li>
              You must use the Cluey voucher by its expiry date (75 days from the date of the original session) or it
              will be forfeited.
            </li>
          </ul>
        </div>
      ),
    },
  },
};
